//import moment from 'moment';
import moment from 'moment-timezone';

/**
 * Formats a date string into a desired format.
 *
 * @param {string|object} input - Either a date string or an options object.
 * @param {string} [expectedFormat='YYYY-MM-DD'] - Format to validate against.
 * @param {string} [returnFormat='MMM D, YYYY'] - Format to return if valid.
 * @param {boolean} [strictParsing=true] - Whether to use strict parsing.
 * @param {string} [defaultDate=''] - Value to return if date is invalid.
 * @returns {string} - Formatted date or defaultDate if invalid.
 */
export const formatDateHelper = (input) => {
  if (!input) {
    return '';
  }
  let date, expectedFormat, returnFormat, strictParsing, defaultDate;

  if (typeof input === 'string') {
    date = input;
    expectedFormat = 'YYYY-MM-DD';
    returnFormat = 'MMM D, YYYY';
    strictParsing = true;
    defaultDate = '';
  } else {
    ({
      date,
      expectedFormat = 'YYYY-MM-DD',
      returnFormat = 'MMM D, YYYY',
      strictParsing = true,
      defaultDate = '',
    } = input);
  }

  if (!date) return defaultDate;

  let parsedDate = moment(date, expectedFormat, strictParsing);

  // If strict format parsing fails, try generic parsing
  if (!parsedDate.isValid()) {
    parsedDate = moment(date);
  }

  return parsedDate.isValid() ? parsedDate.format(returnFormat) : defaultDate;
};

/**
 * Check if earlierDate is within a time threshold of laterDate
 * @param {Object} params
 * @param {string|Date} params.earlierDate - The earlier date to compare
 * @param {string} [params.earlierDateTimeZone='UTC'] - Timezone of earlierDate
 * @param {string|Date} [params.laterDate=now] - The later date to compare
 * @param {string} [params.laterDateTimeZone=Intl.DateTimeFormat().resolvedOptions().timeZone] - Timezone of laterDate
 * @param {Object} [params.timeThreshold={value:1, unit:'days'}] - Threshold {value, unit}, e.g., {value:1, unit:'days'}
 * @returns {boolean} true if within threshold
 */
export const isDateWithinCurrentTimeThreshold = ({
  earlierDate,
  earlierDateTimeZone = 'UTC',
  laterDate,
  laterDateTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  timeThreshold = { value: 1, unit: 'days' },
}) => {
  if (!earlierDate) return false;

  const later = laterDate
    ? moment.tz(laterDate, laterDateTimeZone)
    : moment.tz(laterDateTimeZone);

  const earlier = moment.tz(earlierDate, earlierDateTimeZone);

  const diff = later.diff(earlier, timeThreshold.unit, true); // true for floating point

  return diff <= timeThreshold.value;
};
